import { useState } from "react";
import { Layout, Typography, Button, Space, Card, Statistic, Avatar, Tooltip, Input, Flex, theme, Tag, Modal } from "antd";
import { FileAddOutlined, FilterOutlined, SearchOutlined, CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, DollarOutlined } from "@ant-design/icons";
import NavigationBar from "../../shared/components/NavigationBar";
import HeaderApp from "../../shared/components/HeaderApp";
import HybridCard from "../../shared/components/HybridCard";
import EmptyData from "../../shared/components/EmptyData";
import { useFetchQuotes } from "../hooks/useFetchQuotes";
import Loading from "../../shared/components/Loading";
import DrawerDetails from "../../shared/components/DrawerDetails";
import QuoteDetails from "../components/QuoteDetails";
import HeaderPage from "../../shared/components/HeaderPage";
import { useNavigate } from "react-router-dom";
import type { QuoteData } from "../types";
import { statusQuote } from "../contants/statusQuote";
import { useDeleteQuote } from "../hooks/useDeleteQuote";
import openNotification from "../../../utils/openNotification";
import { resolveQuoteTotals } from "../utils/quoteTotals";

const { Content } = Layout;
const { Text } = Typography;

const MXN = new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" });

const QuotesPage = () => {
  const { token } = theme.useToken();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedQuote, setSelectedQuote] = useState<QuoteData | null>(null);

  const { data: fetchedQuotes, isLoading } = useFetchQuotes();
  const { mutate: removeQuote } = useDeleteQuote();
  const navigate = useNavigate();

  // Only rejected quotes can be deleted (the backend validates it as well).
  const handleDeleteQuote = (quote: QuoteData) => {
    Modal.confirm({
      title: "Eliminar cotización",
      content: "¿Estás seguro de que quieres eliminar esta cotización rechazada?",
      okText: "Sí, eliminar",
      okButtonProps: { danger: true },
      cancelText: "Cancelar",
      onOk: () =>
        new Promise<void>((resolve) => {
          removeQuote(quote.id_quote, {
            onSuccess: () => {
              openNotification("success", "Éxito", "Cotización eliminada correctamente");
              setSelectedQuote((prev) => (prev?.id_quote === quote.id_quote ? null : prev));
              resolve();
            },
            onError: (err) => {
              openNotification("error", "Error", err.message);
              resolve();
            },
          });
        }),
    });
  };

  const quotesList: QuoteData[] = fetchedQuotes || [];

  const completedCount = quotesList.filter(q => q.status_quote === 'completed').length;
  const pendingCount = quotesList.filter(q => q.status_quote === 'pending').length;
  const rejectedCount = quotesList.filter(q => q.status_quote === 'rejected').length;


  const filteredQuotes = quotesList.filter((q) => {
    if (!searchText) return true;
    const lowerSearch = searchText.toLowerCase();
    return (
      q.description_quote.toLowerCase().includes(lowerSearch)
    );
  });

  return (
    <>
      <HeaderApp sectionName={"Cotizaciones"} showBack />
      <Content
        style={{
          marginBottom: 64,
          padding: "24px 16px",
          marginLeft: "auto",
          marginRight: "auto",
          width: "100%",
          backgroundColor: token.colorBgLayout,
        }}
        className="max-width-container"
      >
        <HeaderPage
          title="Cotizaciones"
          subtitle="Administra, revisa y crea cotizaciones para tus clientes."
          actions={[
            {
              label: "Crear Cotización",
              icon: <FileAddOutlined />,
              type: 'primary',
              onClick: () => {
                navigate("/cotizacion/nueva");
              },
            },
          ]}
        />

        <Flex gap={16} wrap style={{ marginBottom: 28 }}>
          <Card style={{ flex: 1, minWidth: 190, borderRadius: token.borderRadiusLG }} styles={{ body: { padding: "16px 20px" } }}>
            <Flex align="center" gap={16}>
              <Avatar size={45} icon={<ClockCircleOutlined />} style={{ background: token.colorWarningBg, color: token.colorWarning, borderRadius: token.borderRadiusLG }} />
              <Statistic title={<Text style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: token.colorTextSecondary }}>Pendientes</Text>} value={pendingCount} valueStyle={{ fontSize: 18, fontWeight: 900, lineHeight: 1 }} />
            </Flex>
          </Card>

          <Card style={{ flex: 1, minWidth: 190, borderRadius: token.borderRadiusLG }} styles={{ body: { padding: "16px 20px" } }}>
            <Flex align="center" gap={16}>
              <Avatar size={45} icon={<CheckCircleOutlined />} style={{ background: token.colorSuccessBg, color: token.colorSuccess, borderRadius: token.borderRadiusLG }} />
              <Statistic title={<Text style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: token.colorTextSecondary }}>Completadas</Text>} value={completedCount} valueStyle={{ fontSize: 18, fontWeight: 900, lineHeight: 1 }} />
            </Flex>
          </Card>

          <Card style={{ flex: 1, minWidth: 190, borderRadius: token.borderRadiusLG }} styles={{ body: { padding: "16px 20px" } }}>
            <Flex align="center" gap={16}>
              <Avatar size={45} icon={<CloseCircleOutlined />} style={{ background: token.colorErrorBg, color: token.colorError, borderRadius: token.borderRadiusLG }} />
              <Statistic title={<Text style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: token.colorTextSecondary }}>Rechazadas</Text>} value={rejectedCount} valueStyle={{ fontSize: 18, fontWeight: 900, lineHeight: 1 }} />
            </Flex>
          </Card>
        </Flex>

        <Flex justify="space-between" align="center" style={{ marginBottom: 12, padding: "0 4px" }}>
          <Text strong style={{ fontSize: 15 }}>Listado de Cotizaciones</Text>
          <Space>
            {searchOpen && (
              <Input
                prefix={<SearchOutlined />}
                placeholder="Buscar cliente..."
                size="small"
                autoFocus
                style={{ borderRadius: 8, width: 200 }}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onBlur={() => { if (!searchText) setSearchOpen(false); }}
              />
            )}
            <Tooltip title="Filtrar">
              <Button type="text" icon={<FilterOutlined />} shape="default" style={{ borderRadius: 8 }} />
            </Tooltip>
            <Tooltip title="Buscar">
              <Button type="text" icon={<SearchOutlined />} shape="default" style={{ borderRadius: 8 }} onClick={() => setSearchOpen(true)} />
            </Tooltip>
          </Space>
        </Flex>

        <Flex vertical gap={12}>
          {isLoading ? (
            <Loading />
          ) : filteredQuotes.length === 0 ? (
            <EmptyData description="No se encontraron cotizaciones" />
          ) : (
            filteredQuotes.map((q) => {
              const status = q.status_quote === 'rejected' ? 'error' : q.status_quote === 'completed' ? 'success' : 'warning';
              return (
                <HybridCard
                  key={q.id_quote}
                  title={q.description_quote}
                  subtitle={`${MXN.format(resolveQuoteTotals(q).total)} MXN`}
                  icon={<DollarOutlined />}
                  status={status}
                  tagStatus={<Tag
                    color={statusQuote[q.status_quote].color}
                    style={{ fontWeight: 700, borderRadius: 999, marginBottom: 8 }}
                  >
                    {statusQuote[q.status_quote].label}
                  </Tag>}
                  onClick={() => setSelectedQuote(q)}
                  swipe={{
                    onView: () => setSelectedQuote(q),
                    onDelete: q.status_quote === 'rejected' ? () => handleDeleteQuote(q) : undefined,
                  }}
                />
              )
            }))}
        </Flex>

        <NavigationBar activeKey="" />

        <DrawerDetails
          title="Detalle de la Cotización"
          open={!!selectedQuote}
          onClose={() => setSelectedQuote(null)}
          contentBody={selectedQuote ? <QuoteDetails quote={selectedQuote} onDeleted={() => setSelectedQuote(null)} /> : null}
        />
      </Content>
    </>
  );
};

export default QuotesPage;
